/**
 * ShareProjectModal — the project's front door for everyone else.
 *
 * Two things make this more than a multi-select:
 *
 *  1. **The custom message.** The invitee sees the system copy ("X invited you
 *     to <project>") plus whatever the inviter types here. An invitation with a
 *     sentence of human context gets accepted; a bare system notice gets
 *     ignored.
 *
 *  2. **The cross-tenant consent step.** Selecting anyone outside the project's
 *     own organization means the agents already working in this project — their
 *     instructions, knowledge files and skills — become reachable by those
 *     people. That is a real exposure, so it is stated at the moment of choice,
 *     names the agents concerned, and must be acknowledged before Send unlocks.
 *     Not a toast afterwards.
 *
 * The picker only ever offers targets the caller is actually allowed to invite:
 * the list comes from `/projects/<id>/share-targets`, which is derived from the
 * same two validators the POST enforces.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslator } from '../hooks/useTranslator';
import projectService from '../services/projectService';
import type { ProjectAgent, ProjectSummary, ShareTargets } from '../types';
import { PersonAvatar } from './PersonAvatar';
import { SkeletonRows, ErrorBlock } from './StateBlocks';
import './ShareProjectModal.css';

interface ShareProjectModalProps {
  isOpen: boolean;
  project: ProjectSummary;
  /** Agents already used in this project — named in the exposure notice. */
  agents: ProjectAgent[];
  onClose: () => void;
  /** Fired after a successful invite so the caller can refresh its board. */
  onInvited: () => void;
}

export function ShareProjectModal({
  isOpen,
  project,
  agents,
  onClose,
  onInvited,
}: ShareProjectModalProps) {
  const { t, tRef } = useTranslator();

  const [targets, setTargets] = useState<ShareTargets | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [partial, setPartial] = useState<string[]>([]);

  const loadTargets = React.useCallback(() => {
    setLoading(true);
    setLoadError(null);
    projectService
      .getShareTargets(project.project_id)
      .then(setTargets)
      .catch(() => setLoadError(
        tRef.current('projects.share.loadFailed')
          || t('projects.share.loadPeopleFailed'),
      ))
      .finally(() => setLoading(false));
    // `t` is deliberately NOT a dependency — see useTranslator.ts. With it here
    // this loader re-fired ~11 times a second for as long as the dialog was open.
  }, [project.project_id, tRef]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSelectedUsers(new Set());
    setSelectedTenants(new Set());
    setMessage('');
    setAcknowledged(false);
    setSendError(null);
    setPartial([]);
    loadTargets();
  }, [isOpen, loadTargets]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, sending]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const users = targets?.users || [];
    if (!q) return users;
    return users.filter(u =>
      (u.user_full_name || '').toLowerCase().includes(q)
      || u.user_email.toLowerCase().includes(q)
      || (u.tenant_name || '').toLowerCase().includes(q));
  }, [targets, query]);

  const filteredTenants = useMemo(() => {
    const q = query.trim().toLowerCase();
    const tenants = targets?.tenants || [];
    if (!q) return tenants;
    return tenants.filter(tn =>
      tn.tenant_name.toLowerCase().includes(q) || tn.tenant_code.toLowerCase().includes(q));
  }, [targets, query]);

  /**
   * Does the current selection reach outside the project's own organization?
   * Selecting a whole tenant other than the owner's counts, and so does any
   * individual whose tenant differs from the project's.
   */
  const reachesOutside = useMemo(() => {
    if (!targets) return false;
    const outsideUser = targets.users.some(
      u => selectedUsers.has(u.user_id) && u.tenant_id !== project.tenant_id,
    );
    const outsideTenant = Array.from(selectedTenants).some(id => id !== project.tenant_id);
    return outsideUser || outsideTenant;
  }, [targets, selectedUsers, selectedTenants, project.tenant_id]);

  const selectionCount = selectedUsers.size + selectedTenants.size;
  const needsAck = reachesOutside && !acknowledged;
  const canSend = selectionCount > 0 && !needsAck && !sending;

  const toggle = (set: Set<string>, id: string, apply: (next: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
    // A selection change can move the invite in or out of cross-tenant
    // territory, so a prior acknowledgement must not carry over.
    setAcknowledged(false);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendError(null);
    setPartial([]);
    try {
      const { result } = await projectService.invite(project.project_id, {
        user_ids: Array.from(selectedUsers),
        tenant_ids: Array.from(selectedTenants),
        invite_message: message.trim() || undefined,
      });

      // Per-target failures are reported individually rather than collapsed into
      // "something went wrong" — the usual cause is a target the caller isn't
      // allowed to reach, and the user deserves to know which one.
      const failures = [
        ...(result.users?.failed || []),
        ...(result.tenants?.failed || []),
      ].map(f => f.error || '').filter(Boolean);

      if (failures.length) {
        setPartial(Array.from(new Set(failures)));
        onInvited();
        return;
      }
      onInvited();
      onClose();
    } catch (err: any) {
      setSendError(
        err?.response?.data?.error
          || (t('projects.share.sendFailed') || 'Could not send the invitations.'),
      );
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="pj-overlay" onClick={() => !sending && onClose()} role="presentation">
      <div
        className="pj-modal pj-modal-wide share-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('projects.share.title') || 'Share project'}
        onClick={e => e.stopPropagation()}
      >
        <header className="pj-modal-header">
          <div className="pj-modal-heading">
            <h2 className="pj-modal-title">
              {(t('projects.share.title') || 'Share “{{project}}”')
                .replace('{{project}}', project.project_name)}
            </h2>
            <p className="pj-modal-subtitle">
              {t('projects.share.subtitle')
                || t('projects.share.inviteExplain')}
            </p>
          </div>
          <button
            type="button"
            className="pj-icon-btn"
            onClick={onClose}
            disabled={sending}
            aria-label={t('common.close') || 'Close'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="pj-modal-body share-body">
          <div className="share-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              dir="auto"
              className="share-search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('projects.share.searchPlaceholder')
                || t('projects.share.searchPlaceholder')}
              aria-label={t('projects.share.searchPlaceholder') || 'Search'}
            />
          </div>

          {/* One fixed-height region for all three states. Without this the
              dialog grew and shrank as the search narrowed — every keystroke
              that changed the match count resized the window under the
              pointer, and the Send button moved while you were reaching for
              it. The region is the height of a full list and scrolls
              internally, so 0 results and 50 results look the same. */}
          <div className="share-results">
          {loading ? (
            <SkeletonRows rows={4} />
          ) : loadError ? (
            <ErrorBlock message={loadError} onRetry={loadTargets} />
          ) : (
            <div className="share-lists">
              {targets?.can_share_with_tenants && filteredTenants.length > 0 && (
                <section className="share-section">
                  <h3 className="share-section-title">
                    {t('projects.share.organizations') || 'Organizations'}
                    <span className="share-section-note">
                      {t('projects.share.organizationsNote')
                        || t('projects.share.orgImmediate')}
                    </span>
                  </h3>
                  <ul className="share-option-list">
                    {filteredTenants.map(tenant => (
                      <li key={tenant.tenant_id}>
                        <label
                          className={`share-option ${tenant.already_member ? 'is-disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTenants.has(tenant.tenant_id)}
                            disabled={tenant.already_member}
                            onChange={() => toggle(selectedTenants, tenant.tenant_id, setSelectedTenants)}
                          />
                          <span className="share-option-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 21h18" />
                              <path d="M5 21V7l7-4 7 4v14" />
                              <path d="M10 21v-5h4v5" />
                            </svg>
                          </span>
                          <span className="share-option-text">
                            <span className="share-option-name" dir="auto">{tenant.tenant_name}</span>
                            <span dir="auto" className="share-option-sub">{tenant.tenant_code}</span>
                          </span>
                          {tenant.already_member && (
                            <span className="pj-pill pj-pill-neutral">
                              {t('projects.share.alreadyShared') || 'Already shared'}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="share-section">
                <h3 className="share-section-title">
                  {t('projects.share.people') || 'People'}
                </h3>
                {filteredUsers.length === 0 ? (
                  <p className="share-none">
                    {query
                      ? (t('projects.share.noMatches') || 'Nobody matches that search.')
                      : (t('projects.share.noTargets')
                        || t('projects.share.nobodyToInvite'))}
                  </p>
                ) : (
                  <ul className="share-option-list">
                    {filteredUsers.map(user => (
                      <li key={user.user_id}>
                        <label
                          className={`share-option ${user.already_member ? 'is-disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.user_id)}
                            disabled={user.already_member}
                            onChange={() => toggle(selectedUsers, user.user_id, setSelectedUsers)}
                          />
                          <PersonAvatar
                            seed={user.user_id}
                            name={user.user_full_name}
                            email={user.user_email}
                            tenantName={user.tenant_name}
                            size={30}
                          />
                          <span className="share-option-text">
                            <span className="share-option-name" dir="auto">
                              {user.user_full_name || user.user_email}
                            </span>
                            <span dir="auto" className="share-option-sub">
                              {user.user_email}
                              {user.tenant_id !== project.tenant_id && user.tenant_name && (
                                <> · <span className="share-option-tenant" dir="auto">{user.tenant_name}</span></>
                              )}
                            </span>
                          </span>
                          {user.already_member && (
                            <span className="pj-pill pj-pill-neutral">
                              {t('projects.share.alreadyInvited') || 'Already invited'}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
          </div>

          <div className="pj-field">
            <label className="pj-label" htmlFor="pj-invite-message">
              {t('projects.share.messageLabel') || 'Add a message'}
              <span className="pj-label-optional"> · {t('common.optional') || 'optional'}</span>
            </label>
            <textarea dir="auto"
              id="pj-invite-message"
              className="pj-textarea"
              style={{ minHeight: 64 }}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('projects.share.messagePlaceholder')
                || t('projects.share.notePlaceholder')}
            />
            <p className="pj-hint">
              {t('projects.share.messageHint')
                || t('projects.share.noteHelp')}
            </p>
          </div>

          {/* The consent step. Rendered only when the selection actually
              reaches outside the project's organization. */}
          {reachesOutside && (
            <div className="pj-notice pj-notice-warning share-consent">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <div className="share-consent-text">
                <strong>
                  {t('projects.share.crossTenantTitle')
                    || t('projects.share.crossTenantTitle')}
                </strong>
                <p>
                  {t('projects.share.crossTenantBody')
                    || 'They will be able to read and continue every conversation here, and to use the agents already working in this project — including those agents’ instructions, knowledge files and skills.'}
                </p>
                {agents.length > 0 && (
                  <p className="share-consent-agents">
                    {t('projects.share.crossTenantAgents') || 'Agents in this project:'}{' '}
                    <span>{agents.map(a => a.chatbot_name).join(', ')}</span>
                  </p>
                )}
                <label className="share-consent-ack">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    {t('projects.share.crossTenantAck')
                      || t('projects.share.crossTenantConsent')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {partial.length > 0 && (
            <div className="pj-notice pj-notice-danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <div>
                <strong>{t('projects.share.partialTitle') || 'Some invitations were not sent'}</strong>
                <ul className="share-partial-list">
                  {partial.map(reason => <li key={reason}>{reason}</li>)}
                </ul>
              </div>
            </div>
          )}

          {sendError && <div className="pj-form-error">{sendError}</div>}
        </div>

        <footer className="pj-modal-footer pj-modal-footer-split">
          <span className="share-count" aria-live="polite">
            {selectionCount > 0
              ? (t('projects.share.selectedCount') || '{{count}} selected')
                  .replace('{{count}}', String(selectionCount))
              : ''}
          </span>
          <span className="share-footer-actions">
            <button type="button" className="pj-btn pj-btn-secondary" onClick={onClose} disabled={sending}>
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="button"
              className="pj-btn pj-btn-primary"
              onClick={handleSend}
              disabled={!canSend}
              title={needsAck
                ? (t('projects.share.ackRequired') || 'Confirm the note above to continue')
                : undefined}
            >
              {sending
                ? (t('projects.share.sending') || 'Sending…')
                : (t('projects.share.send') || 'Send invitations')}
            </button>
          </span>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default ShareProjectModal;
